import gql from "graphql-tag"

const FOUNDER_ACTION_FRAGMENT = gql`
  fragment FounderActionFields on FounderAction {
    id
    title
    notes
    impactScore
    actionRationale
    priority
    status
    source
    createdAt
    completedAt
  }
`

const FOUNDER_ASSIST_FRAGMENT = gql`
  fragment FounderAssistFields on FounderAssist {
    id
    mode
    questionnaireSavedAt
    companyBackground
    companyGoals
    futureExpansion
    currentResources
    peopleAndSkills
    uniqueValueIp
    cashFunding
    companyOtherNotes
    personalBackground
    personalGoals
    interests
    skills
    individualOtherNotes
    summaryParagraph
    sixMonthPlanParagraph
    summaryModifiedAt
    planModifiedAt
    lastMotivationalStatement
    lastCheckinAt
    lastQuarterlyAckAt
    generationInProgress
    generationError
    needsQuarterlyReview
    needsCheckinNudge
    weeklyCheckinPattern
    daysIntoQuarter
    actionsCompletedThisQuarter
    refresh {
      cadence
      limit
      left
      canRefresh
      nextRefreshAt
      checkinOverdue
      daysSinceCheckin
    }
    profileChanges
    actions {
      ...FounderActionFields
    }
  }
  ${FOUNDER_ACTION_FRAGMENT}
`

export const MY_FOUNDER_ASSIST_QUERY = gql`
  query MY_FOUNDER_ASSIST_QUERY {
    myFounderAssist {
      ...FounderAssistFields
    }
  }
  ${FOUNDER_ASSIST_FRAGMENT}
`

export const SAVE_FOUNDER_QUESTIONNAIRE = gql`
  mutation SAVE_FOUNDER_QUESTIONNAIRE($mode: String!, $fields: Json!) {
    saveFounderQuestionnaire(mode: $mode, fields: $fields) {
      ...FounderAssistFields
    }
  }
  ${FOUNDER_ASSIST_FRAGMENT}
`

export const UPDATE_FOUNDER_ASSIST_MODE = gql`
  mutation UPDATE_FOUNDER_ASSIST_MODE($mode: String!) {
    updateFounderAssistMode(mode: $mode) {
      ...FounderAssistFields
    }
  }
  ${FOUNDER_ASSIST_FRAGMENT}
`

export const UPDATE_FOUNDER_ASSIST_PARAGRAPHS = gql`
  mutation UPDATE_FOUNDER_ASSIST_PARAGRAPHS($summary: String, $plan: String) {
    updateFounderAssistParagraphs(summary: $summary, plan: $plan) {
      id
      summaryParagraph
      sixMonthPlanParagraph
      summaryModifiedAt
      planModifiedAt
    }
  }
`

export const UPSERT_FOUNDER_ACTION = gql`
  mutation UPSERT_FOUNDER_ACTION(
    $id: ID
    $title: String
    $notes: String
    $priority: String
    $status: String
  ) {
    upsertFounderAction(
      id: $id
      title: $title
      notes: $notes
      priority: $priority
      status: $status
    ) {
      ...FounderActionFields
    }
  }
  ${FOUNDER_ACTION_FRAGMENT}
`

export const RESTORE_FOUNDER_ACTION = gql`
  mutation RESTORE_FOUNDER_ACTION($id: ID!) {
    restoreFounderAction(id: $id) {
      ...FounderActionFields
    }
  }
  ${FOUNDER_ACTION_FRAGMENT}
`

export const DELETE_FOUNDER_ACTION = gql`
  mutation DELETE_FOUNDER_ACTION($id: ID!) {
    deleteFounderAction(id: $id) {
      success
    }
  }
`

// Starts a background job and returns the FounderAssist with
// generationInProgress = true; poll MY_FOUNDER_ASSIST_QUERY for the outcome.
export const GENERATE_FOUNDER_ASSIST_INITIAL = gql`
  mutation GENERATE_FOUNDER_ASSIST_INITIAL {
    generateFounderAssistInitial {
      ...FounderAssistFields
    }
  }
  ${FOUNDER_ASSIST_FRAGMENT}
`

// A plan refresh: a check-in the member may skip (skip: true sends no answers).
export const SUBMIT_FOUNDER_ASSIST_CHECKIN = gql`
  mutation SUBMIT_FOUNDER_ASSIST_CHECKIN(
    $q1: String
    $q2: String
    $quarterlyChoice: String
    $skip: Boolean
  ) {
    submitFounderAssistCheckin(
      q1: $q1
      q2: $q2
      quarterlyChoice: $quarterlyChoice
      skip: $skip
    ) {
      id
      status
    }
  }
`

// Polled after SUBMIT_FOUNDER_ASSIST_CHECKIN until status leaves "pending".
export const FOUNDER_CHECKIN_JOB_QUERY = gql`
  query FOUNDER_CHECKIN_JOB_QUERY($id: ID!) {
    founderCheckinJob(id: $id) {
      id
      status
      error
      newActionIds
      revisedActions {
        id
        title
        notes
      }
      motivationalStatement
      suggestedSummary
      suggestedPlan
    }
  }
`

// The to-do edits a refresh proposed that the member ticked in the review step.
export const APPLY_FOUNDER_ACTION_REVISIONS = gql`
  mutation APPLY_FOUNDER_ACTION_REVISIONS($checkinId: ID!, $actionIds: [ID!]!) {
    applyFounderActionRevisions(checkinId: $checkinId, actionIds: $actionIds) {
      ...FounderActionFields
    }
  }
  ${FOUNDER_ACTION_FRAGMENT}
`

export const RECORD_QUARTERLY_ACK = gql`
  mutation RECORD_QUARTERLY_ACK {
    recordQuarterlyAck {
      success
    }
  }
`

// Phase 4: write directly to the strategic User fields. After this mutation
// resolves, refetch CURRENT_USER_QUERY so the questionnaire UI sees the new
// values immediately.
export const UPDATE_FOUNDER_PROFILE = gql`
  mutation UPDATE_FOUNDER_PROFILE($fields: Json!) {
    updateFounderProfile(fields: $fields) {
      id
      companyCurrentResources
      companyUniqueValueIp
      companyFutureExpansion
      companyCashFunding
      companyOtherNotes
      personalGoals
      personalInterests
      individualOtherNotes
    }
  }
`

export const MY_FOUNDER_RECOMMENDATIONS_QUERY = gql`
  query MY_FOUNDER_RECOMMENDATIONS_QUERY($kind: String!, $refresh: Boolean) {
    myFounderRecommendations(kind: $kind, refresh: $refresh) {
      kind
      status
      computedAt
      fromCache
      refreshesLeftToday
      llmUsed
      picks {
        refId
        score
        reason
        matchStrength
        founder {
          id
          firstName
          profilePicMed
          location
          locationCountry
          elevatorPitchForStartup
          whyMakeGoodCofounder
          skills
          industryLabel
          stageLabel
          lastActive
        }
        idea {
          id
          title
          elevatorPitch
          industryLabel
          stageLabel
          createdByFirstName
          createdById
          likeCount
          saveCount
        }
      }
    }
  }
`

export { FOUNDER_ACTION_FRAGMENT, FOUNDER_ASSIST_FRAGMENT }
